import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { apiHandler, getApiErrorMessage } from '../../api/apihandler';
import { FEED_POST_PILOT } from '../../api/constants';
import { DEFAULT_TRANSLATION_LANGUAGE, fetchTranslationLanguages } from '../../utilities/translationLanguages.js';
import './aiContentCreatorModal.css';

const DEFAULT_FORM_STATE = {
    keywords: '',
    suggestions: '',
    tone: 'professional',
    style: 'formal',
    language: DEFAULT_TRANSLATION_LANGUAGE.label,
    extra_instructions: ''
};

const TONE_OPTIONS = ['professional', 'friendly', 'encouraging', 'empathetic', 'informative'];
const STYLE_OPTIONS = ['formal', 'casual', 'concise', 'descriptive'];

const AIContentCreatorModal = ({
    show,
    onHide,
    onApply,
    title = 'AI Content Creation',
    targetLabel = 'content',
    defaultKeywords = '',
    // Callers that need a dedicated generator (e.g. the daily report
    // note/comment APIs) override these; everyone else keeps the generic
    // FEED_POST_PILOT endpoint and its `content` response key unchanged.
    generateEndpoint = FEED_POST_PILOT,
    extraPayload = null,
    responseContentKey = 'content'
}) => {
    const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedContent, setGeneratedContent] = useState('');
    const [languages, setLanguages] = useState([]);
    const [languagesLoading, setLanguagesLoading] = useState(false);

    useEffect(() => {
        const fetchLanguages = async () => {
            setLanguagesLoading(true);
            try {
                const { languages: langs } = await fetchTranslationLanguages();
                if (Array.isArray(langs) && langs.length > 0) {
                    setLanguages(langs);
                    setFormData((prev) => ({
                        ...prev,
                        language: langs.some((lang) => lang.label === prev.language) ? prev.language : langs[0].label,
                    }));
                }
            } catch (_) {
                // fallback: keep default
            } finally {
                setLanguagesLoading(false);
            }
        };
        fetchLanguages();
    }, []);

    useEffect(() => {
        if (show) {
            setFormData((prev) => ({
                ...DEFAULT_FORM_STATE,
                language: prev.language || DEFAULT_TRANSLATION_LANGUAGE.label,
                keywords: defaultKeywords || ''
            }));
            setGeneratedContent('');
            setIsGenerating(false);
        }
    }, [show, defaultKeywords]);

    const canGenerate = useMemo(() => formData.keywords.trim().length > 0 && !isGenerating, [formData.keywords, isGenerating]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleGenerate = async () => {
        if (!formData.keywords.trim()) {
            Swal.fire({
                title: 'Keywords Required',
                text: 'Please add keywords to generate AI content.',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        setIsGenerating(true);
        try {
            const payload = {
                keywords: formData.keywords.trim(),
                suggestions: formData.suggestions.trim(),
                tone: formData.tone,
                style: formData.style,
                language: formData.language,
                extra_instructions: formData.extra_instructions.trim(),
                ...(extraPayload || {})
            };

            const response = await apiHandler(generateEndpoint, 'POST', payload);
            const content = response?.data?.data?.[responseContentKey];

            if (response?.data?.response?.status === 200 && content) {
                setGeneratedContent(content);
            } else {
                throw new Error(getApiErrorMessage(response, 'Unable to generate content'));
            }
        } catch (error) {
            Swal.fire({
                title: 'Generation Failed',
                text: error?.message || 'Unable to generate AI content right now.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleUseContent = () => {
        if (!generatedContent.trim()) {
            return;
        }
        onApply(generatedContent);
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            fullscreen="md-down"
            className="ai-content-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="ai-content-modal-title">
                        <span className="material-symbols-outlined">auto_awesome</span>
                        <div>
                            <div>{title}</div>
                            <small>Generate text for {targetLabel}</small>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Group>
                            <Form.Label>Keywords <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Example: developmental milestone, teamwork, creative play"
                                value={formData.keywords}
                                onChange={(e) => handleChange('keywords', e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12}>
                        <Form.Group>
                            <Form.Label>Suggestions</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Optional focus for generated content"
                                value={formData.suggestions}
                                onChange={(e) => handleChange('suggestions', e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={4}>
                        <Form.Group>
                            <Form.Label>Tone</Form.Label>
                            <Form.Select value={formData.tone} onChange={(e) => handleChange('tone', e.target.value)}>
                                {TONE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={4}>
                        <Form.Group>
                            <Form.Label>Style</Form.Label>
                            <Form.Select value={formData.style} onChange={(e) => handleChange('style', e.target.value)}>
                                {STYLE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={4}>
                        <Form.Group>
                            <Form.Label>Language</Form.Label>
                            <Form.Select
                                value={formData.language}
                                onChange={(e) => handleChange('language', e.target.value)}
                                disabled={languagesLoading}
                            >
                                {languagesLoading ? (
                                    <option>Loading...</option>
                                ) : languages.length > 0 ? (
                                    languages.map((lang) => (
                                        <option key={lang.code || lang.id} value={lang.label}>{lang.label}</option>
                                    ))
                                ) : (
                                    <option value={DEFAULT_TRANSLATION_LANGUAGE.label}>{DEFAULT_TRANSLATION_LANGUAGE.label}</option>
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col xs={12}>
                        <Form.Group>
                            <Form.Label>Extra Instructions</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Optional details to guide the AI"
                                value={formData.extra_instructions}
                                onChange={(e) => handleChange('extra_instructions', e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    {generatedContent ? (
                        <Col xs={12}>
                            <div className="ai-generated-preview">
                                <div className="ai-generated-preview-head">Generated Preview</div>
                                <div className="ai-generated-preview-body">{generatedContent}</div>
                            </div>
                        </Col>
                    ) : null}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={isGenerating}>Cancel</Button>
                {generatedContent ? (
                    <Button variant="success" onClick={handleUseContent}>
                        <i className="ri-check-line me-1" />
                        Use This Content
                    </Button>
                ) : null}
                <Button variant="primary" onClick={handleGenerate} disabled={!canGenerate}>
                    <span className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>auto_awesome</span>
                    {isGenerating ? 'Generating...' : 'Generate Content'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AIContentCreatorModal;